import { LanguagePicker } from '@/components/common/LanguagePicker';
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { GeoNexoraLogo } from '@/components/common/GeoNexoraLogo';
import CompanyUsers from '@/components/access/CompanyUsers';
import { currentTenant } from '@/auth/umsSession';
import ums from '@/services/ums.service';

/**
 * Users: the company's own administrator manages its people here - invites, roles, suspends - the way
 * Veramenta's "Users and access" does. It is the company GeoNexora is signed in to; UMS decides what may
 * be changed (`ums.users`).
 */
const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const tenant = currentTenant();
  const access = useQuery({ queryKey: ['umsAuthorization', 'ums', tenant], queryFn: () => ums.authorization('ums'), retry: false });

  return (
    <div className="flex flex-col h-screen bg-gn-tertiary text-gn-primary">
      <header className="h-16 bg-gn-surface border-b border-gn-light flex items-center justify-between px-6 shadow-sm">
        <div className="flex items-center gap-3">
          <GeoNexoraLogo size={26} />
          <span className="font-bold">{t('access.title')}</span>
          {access.data?.tenantName && <span className="text-sm text-gn-muted">· {access.data.tenantName}</span>}
        </div>
        <div className="flex items-center gap-2">
        <LanguagePicker />
        <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gn-blue hover:bg-gn-hover rounded-lg">
          <MapPin size={18} /> {t('access.back_to_map')}
        </Link>
        </div>
      </header>
      <main className="flex-1 overflow-auto p-6 max-w-6xl w-full mx-auto">
        <p className="text-sm text-gn-muted mb-4">{t('access.hint')}</p>
        {!tenant && <p className="text-sm">{t('access.refused.no_ums')}</p>}
        {tenant && access.isError && <p className="text-sm text-gn-danger">{(access.error as Error).message}</p>}
        {tenant && access.data && !access.data.screens?.['ums.users'] && <p className="text-sm">{t('access.refused.permission')}</p>}
        {tenant && access.data?.screens?.['ums.users'] && <CompanyUsers tenantId={tenant} />}
      </main>
    </div>
  );
};

export default UsersPage;
